import React, { useState, useEffect } from 'react';
import { Card, Table, Typography, Space, Button, Tag, Input, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import GlobalBarcodeInput from '@components/operator/GlobalBarcodeInput';
import { useBarcodeInput } from '@contexts/operator/BarcodeInputContext';
import PageHeader from '@shared/PageHeader';
import inboundService from '@services/operator/inboundService';
import { formatDateTime, formatDate } from '@utils/dateTime';
import { ScanOutlined, ArrowRightOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

export default function InboundAcceptanceLanding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [recentInbounds, setRecentInbounds] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');

  // Fetch inbounds with optional search
  const fetchInbounds = async (search = '') => {
    setLoading(true);
    try {
      const params = {
        'inbounds.status': ['pending'],
        page_size: 50
      };
      if (search) {
        params.search = search;
      }
      const data = await inboundService.fetchList(params);

      if (data.success) {
        setRecentInbounds(data.data);
        if (search && data.data.length === 0) {
          message.warning(t('operator.inbound_acceptance.no_match', { defaultValue: '找不到符合的入庫單' }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch inbounds:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInbounds();
  }, []);

  // 掃描後搜尋並刷新列表
  const handleScan = async (code) => {
    if (!code) return;
    setSearchKeyword(code);
    fetchInbounds(code);
    return true;
  };

  // 註冊條碼處理器到全域 BarcodeInput
  useBarcodeInput(handleScan);

  // 清除搜尋
  const handleClearSearch = () => {
    setSearchKeyword('');
    fetchInbounds();
  };

  const columns = [
    {
      title: t('models.inbound.name', { defaultValue: '入庫單號' }),
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <a style={{ fontWeight: 600 }} onClick={() => navigate(`/inbounds/${record.id}`)}>{text}</a>
          {record.extra_name && <Tag>{record.extra_name}</Tag>}
        </Space>
      )
    },
    {
      title: t('models.inbound.merchant', { defaultValue: '貨主' }),
      dataIndex: 'merchant_name',
      key: 'merchant_name',
    },
    {
      title: t('models.inbound.eta', { defaultValue: '預計到貨' }),
      dataIndex: 'eta',
      key: 'eta',
      render: (date) => date ? formatDate(date) : '-'
    },
    {
      title: t('models.inbound.created_at', { defaultValue: '建立時間' }),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => formatDateTime(date)
    },
    {
      title: t('models.inbound.status', { defaultValue: '狀態' }),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'pending' ? 'orange' : 'default'}>
          {t(`enums.inbound.status.${status}`)}
        </Tag>
      )
    },
    {
      title: t('common.actions', { defaultValue: '操作' }),
      key: 'action',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<ArrowRightOutlined />}
          onClick={() => navigate(`/inbounds/${record.id}/acceptance`)}
        >
          {t('common.button.start', { defaultValue: '開始驗收' })}
        </Button>
      )
    }
  ];

  return (
    <div>
      <PageHeader items={['operations', 'operations.inbound_acceptance']} />
      <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>

          {/* Header Section */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <Title level={2}>{t('operator.menu.operations.inbound_acceptance', { defaultValue: '入庫驗收' })}</Title>
            <Text type="secondary">
              {t('operator.inbound_acceptance.subtitle', { defaultValue: '掃描入庫單號或選擇下方的待驗收單據' })}
            </Text>
          </div>

        {/* Scan Section */}
        <Card bordered={false} style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
          <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px 0' }}>
            <GlobalBarcodeInput
              placeholder={t('operator.inbound_acceptance.scan_placeholder', { defaultValue: '請掃描入庫單號或商品條碼...' })}
            />
          </div>
        </Card>

        {/* List Section */}
        <Card
          title={
            <Space>
              <ScanOutlined />
              {searchKeyword
                ? t('operator.inbound_acceptance.search_result', { defaultValue: '搜尋結果：{{keyword}}', keyword: searchKeyword })
                : t('operator.inbound_acceptance.pending_list', { defaultValue: '待驗收入庫單' })
              }
            </Space>
          }
          extra={
            <Space>
              {searchKeyword && (
                <Button onClick={handleClearSearch}>
                  {t('common.button.clear_search', { defaultValue: '清除搜尋' })}
                </Button>
              )}
              <Button onClick={() => fetchInbounds(searchKeyword)} loading={loading}>
                {t('common.button.refresh', { defaultValue: '重新整理' })}
              </Button>
            </Space>
          }
        >
          <Table
            dataSource={recentInbounds}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={false}
            locale={{
              emptyText: searchKeyword
                ? t('operator.inbound_acceptance.no_match', { defaultValue: '找不到符合的入庫單' })
                : t('operator.inbound_acceptance.no_pending', { defaultValue: '目前沒有待驗收的入庫單' })
            }}
          />
        </Card>
        </Space>
      </div>
    </div>
  );
}
